/**
 * Efectos globales de la interfaz que necesitan JavaScript:
 *  - Foco de luz: guarda la posicion del cursor sobre cada .card (--mx/--my).
 *  - Aparicion al desplazarse: las .card que estan fuera de la vista al
 *    montarse aparecen con una animacion cuando el usuario llega a ellas.
 * Respetan "reducir movimiento" (entonces no se activan).
 */
export function installEffects() {
  if (typeof window === 'undefined') return;
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  let frame = 0;
  let last: PointerEvent | null = null;
  window.addEventListener(
    'pointermove',
    (e) => {
      last = e;
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const target = last?.target as Element | null;
        const card = target?.closest?.('.card, .spot, .nav-link') as HTMLElement | null;
        if (!card || !last) return;
        const r = card.getBoundingClientRect();
        card.style.setProperty('--mx', `${last.clientX - r.left}px`);
        card.style.setProperty('--my', `${last.clientY - r.top}px`);
      });
    },
    { passive: true },
  );

  installTabPills();
  if (!reduced) {
    installRipple();
    installTilt();
    installMagnet();
  }

  if (reduced || typeof IntersectionObserver === 'undefined') return;

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target as HTMLElement;
        el.classList.add('reveal-in');
        el.classList.remove('reveal-pending');
        io.unobserve(el);
        // Quita la clase al terminar para no interferir con otros efectos.
        setTimeout(() => el.classList.remove('reveal-in'), 800);
      }
    },
    { rootMargin: '0px 0px -40px 0px', threshold: 0.05 },
  );

  const seen = new WeakSet<Element>();
  function scan(root: ParentNode) {
    root.querySelectorAll?.('main .card').forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      // Solo las que nacen fuera de la pantalla: lo visible ya tiene su entrada.
      if (el.getBoundingClientRect().top > window.innerHeight) {
        el.classList.add('reveal-pending');
        io.observe(el);
      }
    });
  }

  let pending = 0;
  const mo = new MutationObserver(() => {
    if (pending) return;
    pending = requestAnimationFrame(() => {
      pending = 0;
      scan(document);
    });
  });
  mo.observe(document.body, { childList: true, subtree: true });
  scan(document);
}

/**
 * Pestañas con fondo deslizante: a cada [role=tablist] se le agrega una
 * "pastilla" que se mueve hasta la pestaña con aria-selected="true".
 * data-pill="light" usa una pastilla blanca; data-pill="off" lo desactiva.
 */
function installTabPills() {
  function place(list: HTMLElement) {
    if (list.dataset.pill === 'off') return;
    let pill = list.querySelector<HTMLElement>(':scope > .tab-pill');
    const selected = list.querySelector<HTMLElement>(':scope > [aria-selected="true"]');
    if (!selected) {
      pill?.remove();
      return;
    }
    if (!pill) {
      pill = document.createElement('span');
      pill.className = 'tab-pill';
      pill.setAttribute('aria-hidden', 'true');
      if (list.dataset.pill === 'light') pill.classList.add('tab-pill-light');
      list.prepend(pill);
      list.classList.add('has-pill');
      // Primera vez: sin animacion, ya en su lugar.
      pill.style.transition = 'none';
      requestAnimationFrame(() => pill && (pill.style.transition = ''));
    }
    pill.style.left = `${selected.offsetLeft}px`;
    pill.style.top = `${selected.offsetTop}px`;
    pill.style.width = `${selected.offsetWidth}px`;
    pill.style.height = `${selected.offsetHeight}px`;
  }
  function all() {
    document.querySelectorAll<HTMLElement>('[role="tablist"]').forEach(place);
  }
  let pending = 0;
  const schedule = () => {
    if (pending) return;
    pending = requestAnimationFrame(() => {
      pending = 0;
      all();
    });
  };
  new MutationObserver(schedule).observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['aria-selected'],
  });
  window.addEventListener('resize', schedule, { passive: true });
  schedule();
}

/** Onda que se expande desde el punto donde se hace clic en un boton. */
function installRipple() {
  document.addEventListener(
    'pointerdown',
    (e) => {
      const btn = (e.target as Element | null)?.closest?.(
        '.btn-primary, .btn-outline, .btn-secondary, .btn-danger, .btn-ghost',
      ) as HTMLElement | null;
      if (!btn || btn.matches(':disabled')) return;
      const r = btn.getBoundingClientRect();
      const size = Math.max(r.width, r.height) * 2.2;
      const wave = document.createElement('span');
      wave.className = 'ripple';
      wave.style.width = wave.style.height = `${size}px`;
      wave.style.left = `${e.clientX - r.left - size / 2}px`;
      wave.style.top = `${e.clientY - r.top - size / 2}px`;
      if (getComputedStyle(btn).position === 'static') btn.style.position = 'relative';
      btn.style.overflow = 'hidden';
      btn.appendChild(wave);
      wave.addEventListener('animationend', () => wave.remove());
    },
    { passive: true },
  );
}

/** Inclinacion 3D suave de las tarjetas marcadas con .tilt al mover el cursor. */
function installTilt() {
  let current: HTMLElement | null = null;
  const reset = (el: HTMLElement) => {
    el.style.transform = '';
  };
  window.addEventListener(
    'pointermove',
    (e) => {
      // Solo con raton: arrastrar un dedo por la pantalla no debe inclinar tarjetas.
      if (e.pointerType !== 'mouse') return;
      const el = (e.target as Element | null)?.closest?.('.tilt') as HTMLElement | null;
      if (current && current !== el) reset(current);
      current = el;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5;
      const py = (e.clientY - r.top) / r.height - 0.5;
      el.style.transform = `perspective(700px) rotateX(${(-py * 5).toFixed(2)}deg) rotateY(${(px * 6).toFixed(2)}deg) translateY(-2px)`;
    },
    { passive: true },
  );
  document.addEventListener('pointerleave', () => current && reset(current));
}

/** Boton principal "magnetico": se inclina unos pixeles hacia el cursor. Solo con raton. */
function installMagnet() {
  let current: HTMLElement | null = null;
  const reset = (el: HTMLElement) => {
    el.style.translate = '';
  };
  window.addEventListener(
    'pointermove',
    (e) => {
      if (e.pointerType !== 'mouse') return;
      const el = (e.target as Element | null)?.closest?.('.btn-primary:not(:disabled)') as HTMLElement | null;
      if (current && current !== el) reset(current);
      current = el;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const dx = ((e.clientX - (r.left + r.width / 2)) / r.width) * 6;
      const dy = ((e.clientY - (r.top + r.height / 2)) / r.height) * 5;
      el.style.translate = `${dx.toFixed(2)}px ${dy.toFixed(2)}px`;
    },
    { passive: true },
  );
}
